import { EAuthProviderType, type IAuthProviderConfig } from './types';

/**
 * Key of the Auth0 entry. The runtime config synthesises this entry from the
 * flat `AUTH0_*` variables, so it is present even when no provider list is
 * configured, and absent only in a federated-only deployment.
 */
export const AUTH0_PROVIDER_KEY = 'auth0';

const isAuthProviderType = (value: unknown): value is EAuthProviderType =>
  value === EAuthProviderType.AUTH0 || value === EAuthProviderType.OIDC;

const toOptionalString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

const toAuthProviderConfig = (entry: unknown): IAuthProviderConfig | null => {
  if (typeof entry !== 'object' || entry === null) return null;

  const candidate = entry as Record<string, unknown>;
  const key = toOptionalString(candidate.key);

  if (!key || !isAuthProviderType(candidate.type)) return null;

  return {
    key,
    type: candidate.type,
    issuer: toOptionalString(candidate.issuer),
    clientId: toOptionalString(candidate.clientId),
    audience: toOptionalString(candidate.audience),
    // The runtime config publishes the OIDC field as `scopes`; `scope` is
    // accepted too so a hand-written entry matching the OIDC spelling works.
    scope: toOptionalString(candidate.scope ?? candidate.scopes),
    displayName: toOptionalString(candidate.displayName),
  };
};

/**
 * Reads the `authProviders` list off a config object.
 *
 * Defensive by design: the field is injected at container start from an
 * operator-supplied environment variable, and is absent entirely in every
 * environment that has not been migrated. Anything unrecognised is dropped
 * rather than surfaced, so a malformed entry can never break login.
 *
 * @param {unknown} source - Config object that may carry an `authProviders` array
 * @returns {IAuthProviderConfig[]} The well-formed entries, possibly empty
 */
export const readAuthProviders = (source: unknown): IAuthProviderConfig[] => {
  if (typeof source !== 'object' || source === null) return [];

  const { authProviders } = source as { authProviders?: unknown };
  if (!Array.isArray(authProviders)) return [];

  return authProviders
    .map(toAuthProviderConfig)
    .filter((provider): provider is IAuthProviderConfig => provider !== null);
};

/**
 * Finds a configured provider by key.
 *
 * @param {IAuthProviderConfig[]} providers - Entries from {@link readAuthProviders}
 * @param {string | null | undefined} key - Provider key to look for
 * @returns {IAuthProviderConfig | undefined} The matching entry, if any
 */
export const findAuthProvider = (
  providers: IAuthProviderConfig[],
  key: string | null | undefined,
): IAuthProviderConfig | undefined =>
  key ? providers.find(provider => provider.key === key) : undefined;
